import { useInfiniteQuery } from '@tanstack/react-query';

import { api, unwrap } from '@/api';
import { timeAgo } from '@/lib/format';

import { Badge, Button, Card, Spinner } from './ui';

/** `{"changed":["name"],"revision":3}` → "changed name · revision 3". */
function summary(detail: unknown): string {
	if (!detail || typeof detail !== 'object') return '';
	return Object.entries(detail as Record<string, unknown>)
		.map(([k, v]) => `${k} ${Array.isArray(v) ? v.join(', ') : typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}`)
		.join(' · ');
}

const TONE: Record<string, 'neutral' | 'accent' | 'ok' | 'warn' | 'danger'> = {
	create: 'ok',
	delete: 'danger',
	login_failed: 'danger',
	trigger: 'warn',
	import: 'warn',
};
const tone = (action: string) => TONE[action.split('.').pop() ?? ''] ?? 'neutral';

/** Settings: who changed what, newest first, paged with the API cursor. */
export function AuditCard() {
	const audit = useInfiniteQuery({
		queryKey: ['audit'],
		queryFn: ({ pageParam }) => unwrap(api.GET('/api/v1/audit', { params: { query: { cursor: pageParam, limit: 25 } } })),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (last) => last.nextCursor ?? undefined,
		refetchInterval: 15_000,
	});
	const items = audit.data?.pages.flatMap((p) => p.items) ?? [];
	return (
		<Card title="Audit log" className="lg:col-span-2">
			{audit.isPending ? (
				<Spinner />
			) : !items.length ? (
				<p className="text-sm text-fg-muted">No changes yet.</p>
			) : (
				<div className="flex flex-col gap-3">
					<div className="overflow-x-auto">
						<table className="w-full text-xs">
							<thead className="text-left text-fg-muted">
								<tr>
									<th className="py-1 pr-3 font-medium">When</th>
									<th className="py-1 pr-3 font-medium">Who</th>
									<th className="py-1 pr-3 font-medium">Action</th>
									<th className="py-1 pr-3 font-medium">Target</th>
									<th className="py-1 font-medium">Detail</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-border">
								{items.map((e) => {
									const detail = summary(e.detail);
									return (
										<tr key={e.id} className="align-top">
											<td className="py-1.5 pr-3 whitespace-nowrap" title={new Date(e.at).toLocaleString()}>
												{timeAgo(e.at)}
											</td>
											<td className="py-1.5 pr-3 whitespace-nowrap">
												{e.actor}
												{e.ip && <span className="text-fg-muted"> · {e.ip}</span>}
											</td>
											<td className="py-1.5 pr-3 whitespace-nowrap">
												<Badge tone={tone(e.action)}>{e.action}</Badge>
											</td>
											<td className="py-1.5 pr-3 font-mono whitespace-nowrap">{e.targetName ?? '—'}</td>
											<td className="max-w-md truncate py-1.5 text-fg-muted" title={JSON.stringify(e.detail, null, 2)}>
												{detail}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					{audit.hasNextPage && (
						<div>
							<Button size="sm" onClick={() => audit.fetchNextPage()} disabled={audit.isFetchingNextPage}>
								Load more
							</Button>
						</div>
					)}
				</div>
			)}
		</Card>
	);
}
